import { useMemo } from 'react'

import { useBankLineSuggestion } from '@features/reconciliation/hooks'
import { BookkeepingItem } from '@features/reconciliation/types/bookkeepingItem'
import { isCashBasedNonExactSuggestion } from '@features/reconciliation/utils'
import { BankLine } from '@modules-deprecated/bankReconciliation/types'
import { cleanId } from '@modules-deprecated/bankReconciliation/utils'

import { useSandboxModeContext } from '../contexts/sandboxModeContext'

interface UseSandboxBankLineSuggestionProps {
  accountId?: string
  bankLine?: BankLine
  bookkeepingItems?: BookkeepingItem[]
  enabled?: boolean
  organizationId?: string
}

export const useSandboxBankLineSuggestion = ({
  accountId,
  bankLine,
  bookkeepingItems,
  enabled = true,
  organizationId,
}: UseSandboxBankLineSuggestionProps) => {
  const sandbox = useSandboxModeContext()
  const result = useBankLineSuggestion({
    accountId,
    bankLine,
    bookkeepingItems,
    enabled: !sandbox && enabled,
    organizationId,
  })

  const suggestion = useMemo(() => {
    const candidate = sandbox?.sandboxState.suggestions.find((item) =>
      item.transactions.some((transaction) => transaction.id === bankLine?.id),
    )
    const reconcilable = candidate?.reconcilables[0]

    if (!candidate || !reconcilable || !bookkeepingItems) {
      return candidate
    }

    const [, extractedId] = cleanId(reconcilable.id)
    const bookkeepingItem = bookkeepingItems.find((item) => item.id === extractedId)

    return isCashBasedNonExactSuggestion(candidate, bookkeepingItem?.accountingBasis) ? undefined : candidate
  }, [bankLine?.id, bookkeepingItems, sandbox])

  return sandbox ? { suggestion, isLoading: false } : result
}
